import StorageManager from './StorageManager'
import { analytics } from './Analytics/Analytics'
import { fetchDeliveryPromise } from './OrderUtils'
import {
  isOptionExpress,
  isOptionPriority,
  type PartialUploadInformation,
} from '../stores/shippingStore/shipping.utils'
import {
  SHIPPING_OPTION_FREE,
  SHIPPING_TOP_QUEUE_KEY,
  SHIPPING_OPTIONS_KEY,
  SHIPPING_PREFERENCE_KEY,
} from '../stores/shippingStore/shipping.consts'
import { PRODUCT_TYPES } from './ProductTypeState'
import {
  type ShippingOption,
  type ShippingOptionName,
} from '../stores/shippingStore/shipping.types'
import { type Address } from '../stores/addressStore/address.types'

// Partial: express checkout asks with the partial address Stripe shares before payment.
export type DeliveryPromiseAddress = Partial<Address>

export interface DeliveryPromiseRequest {
  productType: PRODUCT_TYPES
  uploads: PartialUploadInformation[]
}

interface FetchProductShippingOptionsParams {
  address: DeliveryPromiseAddress
  deliveryPromiseRequest: DeliveryPromiseRequest
  variantsIds: string[]
  isTopQueueOn: boolean
  customerEmail: string | undefined
  pricingCountry: string
  timeout: number
}

interface RecordShownShippingOptionsParams {
  shippingOptions: ShippingOption[]
  persist: boolean
}

interface StoredShippingOptions {
  shippingOptions?: ShippingOption[]
}

class ShippingOptionsManager {
  fetchProductShippingOptions = async ({
    address,
    deliveryPromiseRequest: { productType, uploads },
    variantsIds,
    isTopQueueOn,
    customerEmail,
    pricingCountry,
    timeout,
  }: FetchProductShippingOptionsParams): Promise<ShippingOption[]> => {
    const shouldContainUploads = productType !== PRODUCT_TYPES.PHOTO_BOOK
    if (shouldContainUploads && uploads.length === 0) {
      return []
    }
    const response: { shippingOptions: ShippingOption[] } =
      await fetchDeliveryPromise({
        address,
        isTopQueueOn,
        uploads,
        variantsIds,
        productType,
        customerEmail,
        pricingCountry,
        timeout,
      })
    return response.shippingOptions
  }

  recordShownShippingOptions({
    shippingOptions,
    persist,
  }: RecordShownShippingOptionsParams): void {
    this.markShippingOptionsSeen(shippingOptions)
    if (persist) {
      StorageManager.set(SHIPPING_OPTIONS_KEY, { shippingOptions })
    }
  }

  private markShippingOptionsSeen(shippingOptions: ShippingOption[]): void {
    const seenShippingProperties: Record<string, boolean> = {}
    if (shippingOptions.some(isOptionExpress)) {
      seenShippingProperties['Has Seen Express Shipping'] = true
    }
    if (shippingOptions.some(isOptionPriority)) {
      seenShippingProperties['Has Seen Priority Shipping'] = true
    }
    if (Object.keys(seenShippingProperties).length > 0) {
      analytics.setUserProperties(seenShippingProperties)
    }
  }

  clearShippingOptions(): void {
    StorageManager.set(SHIPPING_OPTIONS_KEY, null)
    StorageManager.set(SHIPPING_PREFERENCE_KEY, false)
    StorageManager.remove(SHIPPING_TOP_QUEUE_KEY)
  }

  recordShippingOptionsShownForOrder(shippingOptions: ShippingOption[]): void {
    StorageManager.set(SHIPPING_OPTIONS_KEY, { shippingOptions })
  }

  getStoredShippingOptions(): StoredShippingOptions {
    return StorageManager.get(SHIPPING_OPTIONS_KEY) || {}
  }

  getShippingPreference(): ShippingOptionName {
    return (
      StorageManager.get<ShippingOptionName>(SHIPPING_PREFERENCE_KEY) ||
      SHIPPING_OPTION_FREE
    )
  }
}

export const shippingOptionsManager = new ShippingOptionsManager()
